import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import ImportFromWS from "@/components/ImportFromWS/ImportFromWS";
import ModuleSummaryCharts from "@/components/reports/summaryReports/ModuleSummaryCharts";
import { useMainStore } from "@/contexts/Store";
import { useFilters } from "@/hooks/useFilters";
import { useLoading } from "@/hooks/useLoading";
import { useSortingAndPagination } from "@/hooks/useSortingAndPagination";
import { useTableData } from "@/hooks/useTableData";
import { arrayToQueryString, useUpdateFilter } from "@/hooks/useUpdateFilter";
import { Folder } from "@/stores/types/section-tags";

import ExportBulk from "../../dashboard/ExportBulk";
import ImportBulk from "../../dashboard/ImportBulk";
import { getRecordName } from "../../helpers/nameForThemisModuleIdentifier";
import { getRepositionPayloadFromIndices } from "../../helpers/reordering";
import Loading from "../../Loading";
import SlideMenu from "../../slideMenu/SlideMenu";
import MenuActions from "../menu-actions/MenuActions";
import AddNewRow from "../shared/AddNewRow";
import CWCreateFirstCTA from "../shared/create-first-cta/CWCreateFirstCTA";
import CreateFirstCTA from "../shared/CreateFirstCTA";
import { customColumns } from "../shared/custom-columns/CustomColumns";
import ModuleRecordVersion from "../shared/ModuleRecordVersion";
import ModuleTableColumn from "../shared/ModuleTableColumn";
import ReorderableTable from "../shared/ReorderableTable";
import SectionDragContext from "../shared/SectionDragContext";
import TableMenu from "../shared/tableMenu/TableMenu";
import Table from "../Table";
import Folders from "./Folders";

function Documents() {
  // Import MobX stores
  const mainStore = useMainStore();

  useTableData();

  // State
  const [showCheckbox, setShowCheckbox] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [isImportSlideoutOpen, setIsImportSlideoutOpen] = useState(false);
  const [folder, setFolder] = useState<Folder | null>(null);

  // Variables
  const { visibleFields } = mainStore.fields;
  const { list: recordVersions } = mainStore.recordVersions;
  const { isIW, activeWorkspace, moduleWorkspaceID } = mainStore.context;
  const moduleWorkspaces = mainStore.moduleWorkspaces.list;
  const { currentFilterURL } = mainStore.filters;

  // Hooks
  const { currentFilters } = useUpdateFilter();
  const { folder_id } = useParams<{ folder_id: string }>();
  const loading =
    useLoading(visibleFields) ||
    visibleFields.length === 0 ||
    (folder_id != null && folder == null);
  const folderID = Number(folder_id);
  const history = useHistory();
  const location = useLocation();

  const {
    filtersTrigger,
    filtersViewEnabled,
    filtersContent,
    setFiltersViewEnabled,
  } = useFilters({
    fields: visibleFields,
  });

  useEffect(() => {
    (async () => {
      mainStore.documents.setFolderID(folderID);
      if (folderID) {
        setFolder(await mainStore.sectionTags.show(folderID));
      } else {
        setFolder(null);
      }
    })();
  }, [folderID]);

  useEffect(() => {
    mainStore.filters.setCurrentFilterURL(
      currentFilters.length ? arrayToQueryString(currentFilters) : "",
    );
  }, []);

  useEffect(() => {
    history.replace(`${location.pathname}?${currentFilterURL}`);
  }, [location.pathname]);

  const { createColumn, renameColumn, deleteColumn, AddColumnButton } =
    customColumns({});

  const {
    sortByFieldName,
    sortByDirection,
    setSortByFieldName,
    setSortByDirection,
  } = useSortingAndPagination({
    store: mainStore.documents,
    folderID,
  });

  // Functions
  const handleCreateNewDocument = async () => {
    if (activeWorkspace?.id) {
      await mainStore.documents.create({ section_tag_id: folder?.id });
    }
  };

  const resetMenuActions = () => {
    setShowCheckbox(false);
    setSelectMode(false);
    mainStore.dynamicTable.clearSelectedRecordVersionIDs();
  };

  // @ts-expect-error TS(7006) FIXME: Parameter 'recordVersionId' implicitly has an 'any... Remove this comment to see the full error message
  const onSectionChange = (recordVersionId, sectionId) => {
    mainStore.documents.move({
      folderID: sectionId,
      recordVersionID: recordVersionId,
    });
  };

  const onRecordMovedWithinSection = (
    // @ts-expect-error TS(7006) FIXME: Parameter 'recordVersionId' implicitly has an 'any... Remove this comment to see the full error message
    recordVersionId,
    // @ts-expect-error TS(7006) FIXME: Parameter 'sectionId' implicitly has an 'any' type... Remove this comment to see the full error message
    sectionId,
    // @ts-expect-error TS(7006) FIXME: Parameter 'sourceIndex' implicitly has an 'any' ty... Remove this comment to see the full error message
    sourceIndex,
    // @ts-expect-error TS(7006) FIXME: Parameter 'destinationIndex' implicitly has an 'an... Remove this comment to see the full error message
    destinationIndex,
  ) => {
    const relevantRecordVersions = mainStore.recordVersions.list.filter(
      (recordVersion) => {
        if (!sectionId) {
          return !recordVersion.section_tag_id;
        }

        return recordVersion.section_tag_id === sectionId;
      },
    );
    mainStore.recordVersions.reorder(
      recordVersionId,
      getRepositionPayloadFromIndices(
        relevantRecordVersions,
        sourceIndex,
        destinationIndex,
      ),
    );
  };

  const renderFields = visibleFields.map((field) => {
    return (
      <ModuleTableColumn
        key={field.name}
        field={field}
        // @ts-expect-error TS(2322) FIXME: Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
        sortDirection={field.name === sortByFieldName ? sortByDirection : ""}
        setSortByDirection={setSortByDirection}
        setSortByFieldName={setSortByFieldName}
        onDelete={deleteColumn}
        onRename={renameColumn}
      />
    );
  });

  // @ts-expect-error TS(7006) FIXME: Parameter 'recordVersion' implicitly has an 'any' ... Remove this comment to see the full error message
  const renderRow = (recordVersion, index) => {
    const status = mainStore.avroSchemas.firstValueForField(
      "status",
      recordVersion?.data,
    );

    return (
      <ModuleRecordVersion
        key={recordVersion.id}
        fields={visibleFields}
        recordVersion={recordVersion}
        // @ts-expect-error TS(2322) FIXME: Type 'number | null' is not assignable to type 'nu... Remove this comment to see the full error message
        moduleWorkspaceID={moduleWorkspaceID}
        tableID={recordVersion.table_id}
        tableName={recordVersion.table_name}
        moduleIdentifier="documents"
        isDraggable
        isSorted={!!sortByFieldName}
        order={index}
        isLockedRow={status === "published"}
        inSelectMenuActionMode={selectMode}
      />
    );
  };

  return (
    <Table>
      {loading && <Loading loadingLayout="table" showTableHeader={false} />}
      {!loading && (
        <>
          <div className={classNames("table-header-wrap")}>
            <div className="switch-table-wrap" />
            <div className="import-export-buttons-container">
              <ExportBulk isExportFilesEnabled />
              <ImportBulk
                importFromWS
                openImportSlideOut={() => setIsImportSlideoutOpen(true)}
                currentSection={folder?.id}
              />
              {filtersTrigger}
            </div>
          </div>
          <SectionDragContext
            onRecordMoved={onRecordMovedWithinSection}
            onSectionChange={onSectionChange}
          >
            {filtersViewEnabled && (
              <div className="filters-wrap">
                <div className="switch-table-wrap" />
                {filtersContent}
              </div>
            )}
            {moduleWorkspaceID && (
              <ModuleSummaryCharts
                recordVersions={recordVersions}
                moduleWorkspaceID={moduleWorkspaceID}
                sectionTagID={folderID}
                filtersViewEnabled={filtersViewEnabled}
                setFiltersViewEnabled={setFiltersViewEnabled}
                moduleName="documents"
              />
            )}
            <div
              className="table-list-wrap documents-content draggable-documents-content"
              data-testid="documents-content"
            >
              <div className="dropdown-table">
                <div className="list-title-table">
                  <ul>
                    <div className="list-column-wrap">
                      <div className="title-first-block table-column-reorder">
                        {visibleFields?.length > 0 && (
                          <TableMenu
                            setShowCheckbox={setShowCheckbox}
                            resetMenuActions={resetMenuActions}
                            selectMode={selectMode}
                            setSelectMode={setSelectMode}
                          />
                        )}
                      </div>
                      {showCheckbox && selectMode ? (
                        <div>
                          <MenuActions resetMenuActions={resetMenuActions} />
                        </div>
                      ) : (
                        <div className="list-title-table-wrap">
                          {renderFields}
                          <span className="stretch-cell" />
                        </div>
                      )}
                    </div>

                    <AddColumnButton onAdd={createColumn} />
                  </ul>
                </div>

                <AddNewRow
                  dataTestIdPrefix="documents-add-new-documents"
                  recordTypeName={getRecordName(
                    "documents",
                    moduleWorkspaces,
                    true,
                  )}
                  onClick={handleCreateNewDocument}
                  offset={142}
                />

                <ReorderableTable
                  recordVersions={recordVersions}
                  isSorted={!!sortByFieldName}
                  renderer={renderRow}
                  // @ts-expect-error TS(2322) FIXME: Type 'Folder | null' is not assignable to type 'Se... Remove this comment to see the full error message
                  sectionTag={folder}
                />
              </div>

              {recordVersions.length < 3 && isIW && (
                <CreateFirstCTA subject="Document" />
              )}

              {recordVersions.length < 3 && !isIW && (
                <CWCreateFirstCTA
                  title="Add / Share Documents"
                  sentences={[
                    'Create Documents for the collaborative workspace by clicking "Add New Document" on the top to start.',
                    "Shared documents are visible here.",
                  ]}
                />
              )}

              <Folders
                // @ts-expect-error TS(2322) FIXME: Type 'number | null' is not assignable to type 'nu... Remove this comment to see the full error message
                moduleWorkspaceID={moduleWorkspaceID}
                folderID={folder?.id}
              />
            </div>
          </SectionDragContext>
        </>
      )}
      {isImportSlideoutOpen && (
        <SlideMenu
          open={isImportSlideoutOpen}
          closeSlideMenu={() => setIsImportSlideoutOpen(false)}
        >
          <ImportFromWS
            closeSlideOut={() => setIsImportSlideoutOpen(false)}
            refectchData={() =>
              mainStore.documents.index({
                workspaceID: activeWorkspace?.id,
              })
            }
            showFolderIcon
          />
        </SlideMenu>
      )}
    </Table>
  );
}

export default observer(Documents);
